/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, ReactNode } from 'react'

interface Breadcrumb {
  label: string
  url?: string
  path?: string
}

interface BreadcrumbContextType {
  breadcrumbs: Breadcrumb[]
  setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => void
  clearBreadcrumbs: () => void
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined)

/**
 * Breadcrumb context provider
 * Manages breadcrumb state across the application
 * Pages can set their breadcrumbs using setBreadcrumbs()
 */
export const BreadcrumbProvider = ({ children }: { children: ReactNode }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([])

  const value: BreadcrumbContextType = {
    breadcrumbs,
    setBreadcrumbs,
    clearBreadcrumbs: () => setBreadcrumbs([]),
  }

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

/**
 * Hook to use breadcrumb context
 * @returns {BreadcrumbContextType} { breadcrumbs, setBreadcrumbs, clearBreadcrumbs }
 */
export const useBreadcrumb = (): BreadcrumbContextType => {
  const context = useContext(BreadcrumbContext)

  if (!context) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider')
  }

  return context
}

